import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { ReactComponent as BackIcon } from "../../assets/img/backArrow.svg";
import { Radio, RadioChangeEvent } from "antd";
import CustomInput from "../UIKits/CustomInput/CustomInput";
import { Body, Title } from "../UIKits/Typography/Typography";
import Button from "../UIKits/Button/Button";
import TextDetailContainer from "../TextDetailContainer/TextDetailContainer";
import TextDetail from "../TextDetail/TextDetail";
import { formatCurrency } from "../../helper/helper";
import { usePowerPurchaseStore } from "../../store/buyPowerStore";
import { buyPowerAdminSchema } from "../../schema/buy-power.schema";
import { PaymentMethod } from "../Utils/constants";
import { buyPowerSummary, generateInvoice, generateWalletTokenSelfService } from "../../services/selfService";
import { useFundWalletStore } from "../../store/fundWallet";

export const AmountStep = () => {
  const {
    amount,
    meterNumber,
    resetTrigger,
    meterDetails,
    paymentMethod,
    setAmount,
    setMeterNumber,
    setPaymentMethod,
    nextStep,
  } = usePowerPurchaseStore();

  const [loading, setLoading] = useState(false);
  

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      amount: amount || null,
      meterNumber: meterNumber || null,
    },
    resolver: yupResolver(buyPowerAdminSchema),
  });

  useEffect(() => {
    if (resetTrigger > 0) {
      reset({
        amount: null,
        meterNumber: null,
      });
    }
  }, [resetTrigger, reset]);

   const {walletBalance} = useFundWalletStore();

  const watchedAmount = watch("amount");
  const amountOptions = [5000, 10000, 20000, 25000, 40000, 50000];

  const handleProceedToSummary = async (data: any) => {
    setLoading(true);
    try {
      const response = await buyPowerSummary({
        amount: Number(data.amount),
        meterNumber: data.meterNumber,
      });
      if (response?.data?.isSuccessful && response?.data?.data) {
        const summaryData = response.data.data;

        usePowerPurchaseStore.setState({
          meterDetails: {
            meterNumber: summaryData.meterNumber,
            meterName: summaryData.customerName,
            address: summaryData.customerAddress,
            equivalentUnit: summaryData.equivalentUnit,
            amount: summaryData.amount,
            phoneNumber: summaryData.phoneNumber || meterDetails.phoneNumber || "--------",
          },
        });
      }

      setAmount(Number(data.amount));
      setMeterNumber(data.meterNumber);
      nextStep();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || error.response?.data?.errors?.[0] || error.message || "An error occurred";
      toast.error(errorMessage || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentMethodChange = (e: RadioChangeEvent) => {
    setPaymentMethod(e.target.value);
  };

  return (
    <div className="space-y-6">
      <Title level={3}>Buy power</Title>

      <div className="space-y-3.5">
        <CustomInput
          placeholder="Meter number"
          name="meterNumber"
          label="Meter number"
          type="text"
          control={control}
          errorMessage={errors.meterNumber?.message}
        />
      </div>

      {/* Amount Input */}
      <div className="space-y-3.5">
        <CustomInput
          placeholder="Enter or select amount"
          name="amount"
          control={control}
          label="Amount"
          type="number"
          errorMessage={errors.amount?.message}
        />

        {/* Quick Amount Selection */}
        <div className="flex justify-between items-center flex-wrap gap-2">
          {amountOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setValue("amount", option)}
              className={`text-xs font-semibold px-3.5 py-2 bg-[#F9F9F9] hover:bg-primary-50 hover:text-primary-600 transition-all duration-200 rounded-full ${
                Number(watchedAmount) === option
                  ? "bg-primary-100 text-primary-600 border border-primary-500"
                  : "text-foreground"
              }`}
            >
              {formatCurrency(option, 0)}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3.5">
        <p className="text-sm font-medium text-normalBlack">Pay with</p>
        <Radio.Group onChange={handlePaymentMethodChange} value={paymentMethod} className="radio-button flex flex-1">
          <Radio
            value={PaymentMethod.Wallet}
            className="bg-ashbg transition-all duration-300 hover:bg-ash flex py-2 px-4 pr-8 rounded-full group"
          >
            <p className="font-medium text-sm">Wallet</p>
            <p className="text-xs text-[#9A9A9A]">({formatCurrency(walletBalance)})</p>
          </Radio>
          <Radio
            value={PaymentMethod.Remita}
            className="bg-ashbg transition-all duration-300 hover:bg-ash flex py-2 px-4 pr-16 rounded-full group"
          >
            <p className="font-medium text-sm mt-2">Remita</p>
          </Radio>
        </Radio.Group>
      </div>

      {/* Proceed Button */}
      <div className="pt-8">
        <Button
          className="!w-full "
          onClick={handleSubmit(handleProceedToSummary)}
          disabled={loading}
          loading={loading}
        >
          {" "}
          Proceed to summary
        </Button>
      </div>
    </div>
  );
};

//SummaryStep.tsx
interface InvoiceSummaryStepProps {
  onClose: () => void;
  onPaymentSuccess: () => void;
  onPaymentError: () => void;
}
export const SummaryStep: React.FC<InvoiceSummaryStepProps> = ({ onPaymentSuccess, onPaymentError, onClose }) => {
  const {
    amount,
    meterDetails,
    paymentMethod,
    setPaymentResult,
    previousStep,
    setStep,
    nextStep,
    setInvoiceData,
    setLoading,
    triggerRefetch,
  } = usePowerPurchaseStore();

  const [isGenerating, setIsGenerating] = useState(false);
  const [isMakingPayment, setIsMakingPayment] = useState(false);

  const handleMakePayment = async () => {
    setIsMakingPayment(true);
    try {
      const response = await generateWalletTokenSelfService({
        amount: meterDetails.amount || amount,
        meterNo: meterDetails.meterNumber,
      });
      const verificationResponseData = response.data.data;

      setPaymentResult({
        token: verificationResponseData?.token,
        amount: verificationResponseData.amount,
        equivalentUnit: verificationResponseData.equivalentUnit,
        paymentDate: verificationResponseData.paymentDate,
        email: verificationResponseData.email,
        receiptNumber: verificationResponseData?.receiptNumber,
      });
      setStep("success");
      triggerRefetch();
      // onPaymentSuccess()
      toast.success("Payment successful!");
    } catch (error: any) {
      // const errorMessage = error.data?.message || error.message;
      const errorMessage =
        error.response?.data?.message || error.response?.data?.errors?.[0] || error.message || "An error occurred";

      toast.error(errorMessage || "Failed to generate invoice");
    } finally {
      setIsGenerating(false);
      setIsMakingPayment(false);
    }
  };

  const handleGenerateInvoice = async () => {
    setIsGenerating(true);
    setLoading(true);

    try {
      // Call backend to generate invoice and get RRR
      const response = await generateInvoice({
        amount: meterDetails.amount || amount,
        meterNumber: meterDetails.meterNumber,
      });
      const invoiceData = response.data;

      setInvoiceData(invoiceData.data);
      nextStep();
      triggerRefetch();
      toast.success("Invoice generated successfully");
    } catch (error: any) {
       
      const errorMessage = error.response?.data?.message || error.data?.message || error.message;
      toast.error(errorMessage || "Failed to generate invoice");
    } finally {
      setIsGenerating(false);
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}

      <button
        className=" absolute top-9 left-0 p-1 bg-white hover:bg-ash transition-all duration-300 ease-in rounded-full"
        onClick={previousStep}
      >
        <BackIcon />
      </button>

      <Title level={3}>Summary</Title>

      {/* Meter Details */}
      <div className="space-y-3">
        <Body color="tertiary">Meter details</Body>
        <TextDetailContainer className="!text-xs">
          <TextDetail title="Meter No" value={meterDetails.meterNumber} />
          <TextDetail title="Name" value={meterDetails.meterName} />
          <TextDetail title="Address" value={meterDetails.address} />
        </TextDetailContainer>
      </div>

      {/* Payment Details */}
      <div className="space-y-3">
        <Body color="tertiary">Payment details</Body>
        <TextDetailContainer className="!text-xs">
          <TextDetail title="Amount" value={formatCurrency(meterDetails.amount)} />
          <TextDetail title="Equivalent unit" value={meterDetails.equivalentUnit} />
          <TextDetail title="Payment method" value={paymentMethod === PaymentMethod.Wallet ? "Wallet" : "Online"} />
        </TextDetailContainer>
      </div>

      {/* Generate Invoice Button */}

      {paymentMethod === PaymentMethod.Wallet ? (
        <div className="py-4">
          <Button className="!w-full !py-3 !text-sm" onClick={handleMakePayment} disabled={isMakingPayment}>
            {isMakingPayment ? "Processing payment..." : "Make payment"}
          </Button>
        </div>
      ) : (
        <div className="py-4">
          <Button className="!w-full !py-3 !text-sm" onClick={handleGenerateInvoice} disabled={isGenerating}>
            {isGenerating ? "Generating invoice..." : "Generate invoice"}
          </Button>
        </div>
      )}
    </div>
  );
};
